import { z } from 'zod';
import {
  storyboardAspectRatioSchema,
  storyboardAttachmentSchema,
  storyboardAudioSegmentSchema,
  storyboardReferenceSchema,
  storyboardTransitionSchema,
  storyboardApprovalModeSchema,
  storyboardReferenceCategorySchema
} from './storyboard';

export const storyboardBrandAssetSchema = z.object({
  storagePath: z.string(),
  storageBucket: z.string(),
  mimeType: z.string()
});
export type StoryboardBrandAsset = z.infer<typeof storyboardBrandAssetSchema>;

export const storyboardAnalysisJobSchema = z.object({
  storyboardId: z.string(),
  userId: z.string(),
  brief: z.string(),
  durationSec: z.number().int().min(1).max(60),
  sceneDuration: z.number().int().min(1).max(30),
  stylePreset: z.string().optional(),
  aspectRatio: storyboardAspectRatioSchema.default('16:9'),
  references: z.array(storyboardReferenceSchema).optional(),
  attachments: z.array(storyboardAttachmentSchema).optional(),
  approvalMode: storyboardApprovalModeSchema.optional(),
  generationMode: z.enum(['auto', 'directed']).optional(),
  timeoutMs: z.number().int().positive().optional()
});
export type StoryboardAnalysisJob = z.infer<typeof storyboardAnalysisJobSchema>;

export const storyboardAnchorJobSchema = z.object({
  storyboardId: z.string(),
  userId: z.string(),
  anchorPrompt: z.string(),
  stylePreset: z.string().optional(),
  aspectRatio: storyboardAspectRatioSchema.default('16:9'),
  referenceId: z.string().optional(),
  entityId: z.string().optional(),
  category: storyboardReferenceCategorySchema.optional(),
  source: z.string().optional(),
  timeoutMs: z.number().int().positive().optional()
});
export type StoryboardAnchorJob = z.infer<typeof storyboardAnchorJobSchema>;

export const storyboardSceneImageJobSchema = z.object({
  storyboardId: z.string(),
  sceneId: z.string(),
  variationId: z.string().optional(),
  requestToken: z.string().optional(),
  userId: z.string(),
  order: z.number().int().nonnegative(),
  totalScenes: z.number().int().positive(),
  prompt: z.string(),
  userPrompt: z.string().max(6000).optional(),
  refs: z.array(storyboardReferenceSchema).optional(),
  anchorImagePath: z.string().optional(),
  anchorImageBucket: z.string().optional(),
  aspectRatio: storyboardAspectRatioSchema.default('16:9'),
  stylePreset: z.string().optional(),
  durationSec: z.number().int().positive(),
  model: z.string().optional(),
  planVersion: z.number().int().positive().optional(),
  retryCount: z.number().int().min(0).optional()
});
export type StoryboardSceneImageJob = z.infer<typeof storyboardSceneImageJobSchema>;

export const storyboardSceneVideoJobSchema = z.object({
  storyboardId: z.string(),
  sceneId: z.string(),
  variationId: z.string().optional(),
  requestToken: z.string().optional(),
  userId: z.string(),
  prompt: z.string(),
  userPrompt: z.string().max(6000).optional(),
  imageUrl: z.string().url(),
  durationSec: z.number().int().positive().max(30).default(6),
  transition: storyboardTransitionSchema.default('jump_cut'),
  prevFrameUrl: z.string().url().optional(),
  nextFrameUrl: z.string().url().optional(),
  model: z.string().optional(),
  aspectRatio: storyboardAspectRatioSchema.default('16:9'),
  stylePreset: z.string().optional(),
  planVersion: z.number().int().positive().optional(),
  creditEntryId: z.string().optional(),
  creditAmount: z.number().int().optional(),
  useSourceImage: z.boolean().optional(),
  refIdsUsed: z.array(z.string()).optional(),
  refsUsed: z.array(z.string()).optional()
});
export type StoryboardSceneVideoJob = z.infer<typeof storyboardSceneVideoJobSchema>;

export const storyboardPdfJobSchema = z.object({
  storyboardId: z.string(),
  userId: z.string()
});
export type StoryboardPdfJob = z.infer<typeof storyboardPdfJobSchema>;

export const storyboardStitchJobSchema = z.object({
  storyboardId: z.string(),
  userId: z.string(),
  clips: z.array(
    z.object({
      sceneId: z.string(),
      storagePath: z.string(),
      storageBucket: z.string().optional(),
      mediaType: z.enum(['video', 'image']).default('video'),
      durationSec: z.number().int().positive(),
      order: z.number().int().nonnegative(),
      transition: storyboardTransitionSchema
    })
  ),
  audio: z
    .object({
      videoMuted: z.boolean().optional(),
      voiceMuted: z.boolean().optional(),
      voiceSegments: z.array(storyboardAudioSegmentSchema).optional(),
      sceneAudioMutes: z.record(z.string(), z.boolean()).optional()
    })
    .optional(),
  outputFormat: z.enum(['mp4', 'webm']).default('mp4'),
  resolution: z.enum(['720p', '1080p']).default('1080p')
});
export type StoryboardStitchJob = z.infer<typeof storyboardStitchJobSchema>;
